import React from "react"

import FormLabel from "../../resources/FormLabel"
import ReactSelectionElement from "../../../../shared_components/widgets/ReactSelectionElement"
import { equalArrays, isNumber } from "../../../../shared_components/utils"
import { toSelectArray } from "./utils"
import InputFormField from "../../resources/InputFormField"
import { useSensePageWidth } from "../../../samplemanagement/SampleScheduleTab/FormSamplePoint"

export default function RunMetadataForm({
  intl,
  state,
  updateFormValue,
  activeMetadataFields = {},
  initialState = {},
  isPlaceholderRun = false,
}) {
  const pageWidth = useSensePageWidth()
  const chunkSize = pageWidth > 1200 ? 3 : 2
  const colClass = pageWidth > 1200 ? "col-4" : "col-6"
  return (
    <>
      {Object.keys(activeMetadataFields).length > 0 && !isPlaceholderRun && (
        <>
          <hr />
          <FormLabel description={"Metadata"} label={"general.metadata"} />
          {[].concat // Chunking extra input fields into groups of 'chunkSize'
            .apply(
              [],
              Object.entries(activeMetadataFields).map(function (elem, i) {
                return i % chunkSize ? [] : [Object.entries(activeMetadataFields).slice(i, i + chunkSize)]
              }),
            )
            .map((chunk, chunk_idx) => {
              return (
                <div className={"row"} key={`row${chunk_idx}`}>
                  {chunk.map((field) => {
                    const key = field[0]
                    const { menu_type, field_info, allow_custom_input, options, label, required } = field[1]
                    const warningField = Object.keys(state).includes(`${key}Warn`) ? `${key}Warn` : null
                    const warning = state[warningField]
                    let value = state[key]
                    let initial = initialState[key]
                    let hasChanged = false
                    const cleanSingleArray = (arr) => {
                      if (arr.length > 1) {
                        return [arr.join("; ")]
                      }
                      return arr
                    }
                    if (["multi-dropdown", "dropdown"].includes(menu_type)) {
                      value = toSelectArray(value)
                      initial = toSelectArray(initial)
                      hasChanged = !equalArrays(value, initial)
                      if (menu_type === "dropdown") {
                        initial = cleanSingleArray(initial)
                        value = cleanSingleArray(value)
                      }
                    } else {
                      hasChanged = value !== initial
                    }
                    if (["text", "textarea", "number"].includes(menu_type)) {
                      if (Array.isArray(value)) {
                        // Previous field type was dropdown/multi-dropdown - Adjust current value for
                        // display in non-dropdown field assignment
                        value = value.join("; ")
                      }
                      return (
                        <div className={colClass} key={key}>
                          <InputFormField
                            intl={intl}
                            type={menu_type}
                            description={label || key}
                            value={value}
                            hasChanged={hasChanged}
                            updateFormValue={updateFormValue}
                            field={key}
                            warning={warning}
                            warningField={warningField}
                            canEdit={true}
                          />
                          {value && menu_type === "number" && !isNumber(value) && (
                            // Need to show previous text value - won't appear in an input of type 'number'
                            // which for some reason this field now is set as, but wasn't previously...
                            <div>
                              <textarea style={{ width: "100%" }} rows={2} value={value} disabled={true} />
                            </div>
                          )}
                        </div>
                      )
                    }
                    const isMulti = menu_type === "multi-dropdown",
                      // In case old options aren't available in current iteration of field OR a creation
                      // of an option outside the defaults is allowed
                      archived_options = [...new Set([...initial, ...value])].filter(
                        (o) => !options.includes(o),
                      ),
                      finalOptions = [...(options || []), ...archived_options].map((opt) => {
                        if (archived_options.includes(opt)) {
                          return { value: opt, isCustomOption: true }
                        }
                        return { value: opt }
                      })
                    return (
                      <div className={colClass} key={key}>
                        {["multi-dropdown", "dropdown"].includes(menu_type) && (
                          <ReactSelectionElement
                            name={`${key}Selection`}
                            list={finalOptions}
                            label={
                              <span>
                                {label || key}
                                {allow_custom_input && (
                                  <>
                                    &nbsp;
                                    <span
                                      className={"fa fa-plus-circle"}
                                      title={"Can create custom option(s)"}
                                    />
                                  </>
                                )}
                              </span>
                            }
                            hasChanged={hasChanged}
                            boldLabel={true}
                            onValueChange={(_, val) => {
                              const sel = isMulti
                                ? val.length > 0
                                  ? val.map((opt) => opt?.value)
                                  : []
                                : val?.value || ""
                              updateFormValue(null, key, sel, warningField)
                            }}
                            divStyle={warning ? {} : { marginBottom: "1rem" }}
                            value={finalOptions.filter((opt) => value.includes(opt.value))}
                            required={required || false}
                            isMulti={isMulti || false}
                            info_desc={field_info}
                            show_required_msg={warning}
                            onCreateOption={
                              allow_custom_input
                                ? (val) => {
                                    const sel = isMulti ? [...new Set([...value, val])] : val
                                    updateFormValue(null, key, sel, warningField)
                                  }
                                : null
                            }
                            customStyles={{
                              option: (provided, state) => {
                                let colorMap = {}
                                if (state.data.__isNew__ || state.data.isCustomOption) {
                                  colorMap = {
                                    backgroundColor: "var(--lanzalake-orange)",
                                  }
                                }

                                return {
                                  ...provided,
                                  ...colorMap,
                                }
                              },
                            }}
                            classNames={{
                              multiValue: (state) => {
                                return state.data.isCustomOption ? "is_custom_selection" : ""
                              },
                              singleValue: (state) => {
                                return state.data.isCustomOption ? "is_custom_selection" : ""
                              },
                            }}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
        </>
      )}
    </>
  )
}
